import { useState, useRef } from "react";
import RecordRTC from "recordrtc";

interface AttachedFile {
  file: File;
  renamedName: string;
}

interface UseAudioRecorderProps {
  onAudioReady: (audio: AttachedFile) => void;
  formatFileNameByType: (file: File) => string | null;
  notificationSystemRef: React.MutableRefObject<any>;
}

export const useAudioRecorder = ({
  onAudioReady,
  formatFileNameByType,
  notificationSystemRef,
}: UseAudioRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recorder, setRecorder] = useState<InstanceType<
    typeof RecordRTC
  > | null>(null); // ✅ Tipado correcto
  const [stream, setStream] = useState<MediaStream | null>(null);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = async () => {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      const newRecorder = new RecordRTC(micStream, {
        type: "audio",
        mimeType: "audio/wav",
        recorderType: RecordRTC.StereoAudioRecorder,
        desiredSampRate: 16000,
        numberOfAudioChannels: 1,
      });

      newRecorder.startRecording();
      setRecorder(newRecorder);
      setStream(micStream);
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("🎤 Error al acceder al micrófono:", err);
      setIsRecording(false);
      notificationSystemRef?.current?.addNotification?.({
        message: "No se pudo acceder al micrófono.",
        level: "error",
      });
    }
  };

  const stopRecording = () => {
    if (!recorder) return;

    recorder.stopRecording(() => {
      const audioBlob = recorder.getBlob();
      const fileName = `audio-${new Date().toISOString()}.wav`;
      const audioFile = new File([audioBlob], fileName, { type: "audio/wav" });

      const renamed = formatFileNameByType(audioFile);
      if (!renamed) {
        notificationSystemRef?.current?.addNotification?.({
          message: "Formato de audio no permitido.",
          level: "error",
        });
        return;
      }

      onAudioReady({ file: audioFile, renamedName: renamed });

      stream?.getTracks().forEach((track) => track.stop());
      setStream(null);
      setRecorder(null);
      setIsRecording(false);
      setRecordingTime(0);
      if (timerRef.current) clearInterval(timerRef.current);
    });
  };

  const toggleRecording = () => {
    isRecording ? stopRecording() : startRecording();
  };

  return {
    isRecording,
    recordingTime,
    toggleRecording,
  };
};
